import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import VideoEmbed from "./VideoEmbed";
import CreatorChip from "./CreatorChip";

/**
 * SocialRail — "Seen in Reels" horizontal scroll on the homepage.
 * Hides silently if there is no content.
 */
export default function SocialRail() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const viewedRef = useRef(new Set());

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/social/feed", {
          params: { display_type: "homepage", limit: 10 },
        });
        if (alive) setRows(data?.items || []);
      } catch {
        /* silent — rail simply hides */
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const trackView = (id) => {
    if (viewedRef.current.has(id)) return;
    viewedRef.current.add(id);
    api.post(`/social/content/${id}/interaction`, { action_type: "view" }).catch(() => {});
  };

  if (loading || rows.length === 0) return null;

  return (
    <section
      className="px-6 md:px-12 lg:px-24 py-16 bg-[hsl(var(--kb-surface))]"
      data-testid="home-social-rail"
    >
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Seen in Reels</div>
          <h2 className="font-display text-3xl lg:text-4xl">Watch · Love · Shop.</h2>
          <p className="text-sm text-[hsl(var(--kb-muted))] mt-2 max-w-md">
            Short stories from our ateliers and stylists — tap a reel, shop the look.
          </p>
        </div>
        <Link
          to="/reels"
          className="text-sm uppercase tracking-widest underline"
          data-testid="home-social-rail-see-all"
        >
          See all reels →
        </Link>
      </div>

      <div className="flex gap-4 overflow-x-auto snap-x pb-4 -mx-2 px-2 kb-scroll-hide">
        {rows.map((c) => (
          <div key={c.id} className="snap-start shrink-0 w-64 md:w-72">
            <Link
              to={`/reels?content=${c.id}`}
              onClick={() => trackView(c.id)}
              className="block"
              data-testid={`home-social-reel-${c.id}`}
            >
              <VideoEmbed content={c} aspect="9x16" testid={`rail-video-${c.id}`} />
            </Link>
            {Array.isArray(c.linked_products) && c.linked_products.length > 0 && (
              <div className="mt-3 text-xs text-[hsl(var(--kb-muted))]">
                Shop {c.linked_products.length} {c.linked_products.length === 1 ? "piece" : "pieces"} from this look
              </div>
            )}
            {c.creator && (
              <div className="mt-2">
                <CreatorChip creator={c.creator} size="sm" />
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
